import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectChatInitialized, setChatInitialized } from 'store/messages'
import { WidgetLauncher } from 'components/WidgetLauncher'
import { getUserToken } from 'utils/query-params'
import { WidgetContent } from 'WidgetContent'
import { WidgetHelpMessage } from 'components/WidgetHelpMessage'
import { selectIsOpened } from 'store/widget'
import { getMyChats } from 'api/chats'
import { useGetBotMode } from 'hooks/useGetBotMode'
import { settingsActions } from 'store/settings'

export const useConfigureChat = () => {
  const dispatch = useDispatch()
  const chatInitialized = useSelector(selectChatInitialized)

  const initializeChat = useCallback(async () => {
    const userToken = getUserToken()
    if (chatInitialized) return
    if (userToken) {
      await getMyChats({})
      dispatch(setChatInitialized(true))
      return
    }
  }, [chatInitialized])

  return useMemo(() => initializeChat, [initializeChat])
}

type WidgetLayoutProps = {
  areaId?: string
  filterChatsByAreaId?: boolean
}

export const WidgetLayout = ({ areaId, filterChatsByAreaId }: WidgetLayoutProps) => {
  const isOpened = useSelector(selectIsOpened)
  const { isAppMode, botMode } = useGetBotMode()
  const dispatch = useDispatch()

  console.debug('[WidgetLayout] render', {
    areaId,
    filterChatsByAreaId,
    isOpened,
    isAppMode,
    botMode,
    launcherWillRender: !isAppMode,
    contentDisplay: isOpened || isAppMode ? 'block' : 'none',
  })

  useEffect(() => {
    dispatch(settingsActions.setAreaId(areaId))

    if (filterChatsByAreaId) {
      getMyChats({ areaId }).catch(console.error)
    } else if (getUserToken()) {
      // Logged-in users: prefetch chats so the launcher unread dot reflects the unread state even before
      // the widget is opened (e.g. an operator replied to a ticket while the user was away).
      getMyChats({}).catch(console.error)
    }
  }, [areaId, dispatch])

  return (
    <>
      <WidgetHelpMessage key={'help'} />

      <div style={{ display: isOpened || isAppMode ? 'block' : 'none' }}>
        <WidgetContent key={'content'} />
      </div>

      {!isAppMode && <WidgetLauncher key={'launcher'} />}
    </>
  )
}
